import type { FC } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';

import { getAnnouncements } from '@/api/announcements';
import { useDialog } from '@/hooks/v2/use-dialog';
import { Seo } from '@/components/seo';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { Paper } from '@mui/material';

import { AnnouncementsTable } from './components/announcements-table';
import CreateAnnouncementDialog from './dialogs/CreateAnnouncementDialog';

const AnnouncementsPage: FC = () => {
  const dialog = useDialog();
  const { data: announcements, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => getAnnouncements(),
  });

  return (
    <>
      <Seo title="Announcements" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>Announcements</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                  Manage and send community announcements and notifications
                </Typography>
              </Box>
              <RoleGuard
                requiredAccountRoles={[
                  ACCOUNT_ROLES.ACCOUNT_OWNER,
                  ACCOUNT_ROLES.ACCOUNT_ADMIN,
                  ACCOUNT_ROLES.ACCOUNT_MANAGER,
                ]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]}
              >
                <Button
                  onClick={dialog.handleOpen}
                  size="small"
                  startIcon={<PlusIcon size={16} />}
                  variant="contained"
                >
                  New Announcement
                </Button>
              </RoleGuard>
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            <AnnouncementsTable isLoading={isLoading} rows={announcements ?? []} />
          </Paper>
        </Container>
      </Box>

      <CreateAnnouncementDialog onClose={dialog.handleClose} open={dialog.open} />
    </>
  );
};

export default AnnouncementsPage;
